import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from '@/components/ui';
import type { ToastType } from '../../../contexts/ToastContext';
import { bulkAddNoteToPcrs, bulkClosePcrs, bulkLinkWorkId } from '../../../services/pcrService';
import { workIdService, type WorkIdListItem } from '../../../services/workIdService';
import PcrBulkHistoryModal from './PcrBulkHistoryModal';
import PcrBulkCreateWorkIdModal from './PcrBulkCreateWorkIdModal';

function summarizeBulkErrors(errors: string[], maxLines = 4): string {
  if (errors.length === 0) return '';
  const lines = errors.slice(0, maxLines);
  const tail = errors.length > maxLines ? ` (+${errors.length - maxLines} more)` : '';
  return ` ${lines.join(' ')}${tail}`;
}

/** When `clearSelection` is false, keep row checkboxes selected (e.g. total bulk failure). */
export type BulkToolbarSuccessOptions = { clearSelection?: boolean };

export interface PcrBulkActionsToolbarProps {
  selectedIds: number[];
  onClearSelection: () => void;
  onSuccess: (message: string, toastType?: ToastType, options?: BulkToolbarSuccessOptions) => void;
  canClose?: boolean;
  canAddNote?: boolean;
  canLinkWorkId?: boolean;
}

const PcrBulkActionsToolbar: React.FC<PcrBulkActionsToolbarProps> = ({
  selectedIds,
  onClearSelection,
  onSuccess,
  canClose = false,
  canAddNote = false,
  canLinkWorkId = false,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeNote, setCloseNote] = useState('');

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkWorkIdList, setLinkWorkIdList] = useState<WorkIdListItem[]>([]);
  const [linkWorkIdListLoading, setLinkWorkIdListLoading] = useState(false);
  const [linkWorkIdSearch, setLinkWorkIdSearch] = useState('');
  const [linkSelectedWorkId, setLinkSelectedWorkId] = useState('');

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCreateWorkIdModal, setShowCreateWorkIdModal] = useState(false);

  const count = selectedIds.length;

  const fetchLinkWorkIdList = useCallback(async () => {
    setLinkWorkIdListLoading(true);
    try {
      const list = await workIdService.getList({ limit: 5000 });
      setLinkWorkIdList(list);
    } catch {
      setLinkWorkIdList([]);
    } finally {
      setLinkWorkIdListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showLinkModal) return;
    setLinkWorkIdSearch('');
    setLinkSelectedWorkId('');
    setError(null);
    void fetchLinkWorkIdList();
  }, [showLinkModal, fetchLinkWorkIdList]);

  const filteredLinkWorkIds = useMemo(() => {
    let list = linkWorkIdList;
    if (linkWorkIdSearch.trim()) {
      const q = linkWorkIdSearch.trim().toLowerCase();
      list = list.filter(
        (w) =>
          (w.workIdentificationId || '').toLowerCase().includes(q) ||
          String(w.id).includes(q) ||
          (w.comments || '').toLowerCase().includes(q) ||
          (w.status || '').toLowerCase().includes(q) ||
          (w.foremanArea?.name || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [linkWorkIdList, linkWorkIdSearch]);

  const selectedLinkWorkIdRow = linkWorkIdList.find((w) => String(w.id) === linkSelectedWorkId);

  const handleBulkClose = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await bulkClosePcrs(selectedIds, closeNote.trim() || undefined);
      setShowCloseModal(false);
      setCloseNote('');
      const failed = result.errors.length;
      const ok = result.successCount;
      if (ok > 0) {
        onClearSelection();
      }
      const toastType: ToastType =
        failed === 0 ? 'success' : ok === 0 ? 'error' : 'warning';
      onSuccess(
        `Closed ${ok} PCR${ok !== 1 ? 's' : ''}.${failed ? ` ${failed} failed.` : ''}${summarizeBulkErrors(result.errors)}`,
        toastType,
        ok === 0 ? { clearSelection: false } : undefined,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk close failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bulkAddNoteToPcrs(selectedIds, noteText.trim());
      setShowNoteModal(false);
      setNoteText('');
      const failed = result.errors.length;
      const ok = result.successCount;
      if (ok > 0) {
        onClearSelection();
      }
      const toastType: ToastType =
        failed === 0 ? 'success' : ok === 0 ? 'error' : 'warning';
      onSuccess(
        `Added note to ${ok} PCR${ok !== 1 ? 's' : ''}.${failed ? ` ${failed} failed.` : ''}${summarizeBulkErrors(result.errors)}`,
        toastType,
        ok === 0 ? { clearSelection: false } : undefined,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk add note failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkLink = async () => {
    const workIdId = parseInt(linkSelectedWorkId, 10);
    if (isNaN(workIdId) || workIdId < 1) {
      setError('Please select a Work ID from the list.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await bulkLinkWorkId(selectedIds, workIdId);
      const label = String(result.workIdentificationId ?? workIdId);
      const failed = result.errors.length;
      const ok = result.successCount;

      if (ok === 0) {
        setError(
          failed
            ? `Could not link any PCRs to Work ID ${label}.${summarizeBulkErrors(result.errors)}`
            : `Could not link any PCRs to Work ID ${label}.`,
        );
        onSuccess(
          `Link failed: 0 of ${selectedIds.length} PCRs linked to Work ID ${label}.${summarizeBulkErrors(result.errors)}`,
          'error',
          { clearSelection: false },
        );
        return;
      }

      setShowLinkModal(false);
      setLinkSelectedWorkId('');
      setLinkWorkIdSearch('');
      onClearSelection();
      const toastType: ToastType = failed > 0 ? 'warning' : 'success';
      onSuccess(
        failed > 0
          ? `Linked ${ok} of ${selectedIds.length} PCRs to Work ID ${label}.${summarizeBulkErrors(result.errors)}`
          : `Linked ${ok} PCR${ok !== 1 ? 's' : ''} to Work ID ${label}.`,
        toastType,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk link Work ID failed.');
    } finally {
      setBusy(false);
    }
  };

  if (count === 0) return null;

  return (
    <>
      <div
        className="tw-flex tw-items-center tw-w-full tw-px-3 tw-py-2 tw-flex-wrap tw-gap-2"
        style={{
          background: 'linear-gradient(135deg, #eff6ff 0%, #f8faff 100%)',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
        }}
      >
        {/* Left: selection count */}
        <span className="tw-font-semibold tw-text-primary tw-mr-2" style={{ fontSize: '0.875rem', flexShrink: 0 }}>
          <i className="bi bi-check2-square tw-mr-1" />
          {count} PCR{count !== 1 ? 's' : ''} selected
        </span>

        {/* Right: action buttons + clear */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-ml-auto">
          {canAddNote && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowHistoryModal(true)}
              disabled={busy}
              title="View notes and history for selected PCRs"
            >
              <i className="bi bi-clock-history tw-mr-1" />
              History
            </Button>
          )}
          {canClose && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setShowCloseModal(true)}
              disabled={busy}
            >
              <i className="bi bi-x-circle tw-mr-1" />
              Close without Work ID
            </Button>
          )}
          {canAddNote && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowNoteModal(true)}
              disabled={busy}
            >
              <i className="bi bi-chat-left-text tw-mr-1" />
              Add note
            </Button>
          )}
          {canLinkWorkId && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setError(null);
                setShowLinkModal(true);
              }}
              disabled={busy}
            >
              <i className="bi bi-link-45deg tw-mr-1" />
              Link to Work ID
            </Button>
          )}
          {canLinkWorkId && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateWorkIdModal(true)}
              disabled={busy}
            >
              <i className="bi bi-plus-circle tw-mr-1" />
              Create Work ID
            </Button>
          )}
          <button
            type="button"
            className="tw-bg-transparent tw-border-0 tw-text-content-muted tw-p-0"
            onClick={onClearSelection}
            title="Clear selection"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {error && (
          <Alert variant="danger" className="tw-w-full tw-mb-0 tw-py-1 tw-text-sm">
            {error}
          </Alert>
        )}
      </div>

      {/* Bulk Close Modal */}
      <Modal show={showCloseModal} onHide={() => !busy && setShowCloseModal(false)} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title className="tw-text-base">
            Close {count} PCR{count !== 1 ? 's' : ''} without Work ID
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            This will finalize all selected PCRs as closed with no Work ID. Add an optional note to explain why no work was required.
          </p>
          <Form.Group controlId="bulk-close-note">
            <Form.Label className="tw-text-sm tw-font-semibold">Note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
              placeholder="e.g. No action required — road conditions acceptable"
              disabled={busy}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloseModal(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void handleBulkClose()} disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" className="tw-mr-1" /> : null}
            Close {count} PCR{count !== 1 ? 's' : ''}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Add Note Modal */}
      <Modal show={showNoteModal} onHide={() => !busy && setShowNoteModal(false)} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title className="tw-text-base">
            Add note to {count} PCR{count !== 1 ? 's' : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            The same note will be added to the history of all selected PCRs.
          </p>
          <Form.Group controlId="bulk-note-text">
            <Form.Label className="tw-text-sm tw-font-semibold">Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note…"
              disabled={busy}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleBulkNote()}
            disabled={busy || !noteText.trim()}
          >
            {busy ? <Spinner animation="border" size="sm" className="tw-mr-1" /> : null}
            Add note to {count} PCR{count !== 1 ? 's' : ''}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk History Modal */}
      <PcrBulkHistoryModal
        visible={showHistoryModal}
        selectedIds={selectedIds}
        onClose={() => setShowHistoryModal(false)}
      />

      {/* Bulk Create Work ID Modal */}
      <PcrBulkCreateWorkIdModal
        visible={showCreateWorkIdModal}
        selectedIds={selectedIds}
        onClose={() => setShowCreateWorkIdModal(false)}
        onSuccess={(msg, toastType, opts) => {
          setShowCreateWorkIdModal(false);
          if (opts?.clearSelection !== false) {
            onClearSelection();
          }
          onSuccess(msg, toastType, opts);
        }}
      />

      {/* Bulk Link Work ID Modal */}
      <Modal show={showLinkModal} onHide={() => !busy && setShowLinkModal(false)} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title className="tw-text-base">
            Link {count} PCR{count !== 1 ? 's' : ''} to a Work ID
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            All selected PCRs will be linked to the same Work ID and finalized. Search and choose a
            Work ID from the list (same pattern as merging Work IDs).
          </p>
          <Form.Group controlId="bulk-link-work-id">
            <Form.Label className="tw-text-sm tw-font-semibold">Work ID</Form.Label>
            {linkWorkIdListLoading ? (
              <div className="tw-text-center tw-py-3 tw-text-content-muted tw-text-sm">
                <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden />
                Loading work IDs…
              </div>
            ) : (
              <>
                <Form.Control
                  type="text"
                  placeholder="Search by ID, display name, comments, status, or foreman area…"
                  value={linkWorkIdSearch}
                  onChange={(e) => setLinkWorkIdSearch(e.target.value)}
                  className="tw-mb-2"
                  size="sm"
                  disabled={busy}
                />
                <Form.Select
                  value={linkSelectedWorkId}
                  onChange={(e) => setLinkSelectedWorkId(e.target.value)}
                  size="sm"
                  style={{ height: 220 }}
                  htmlSize={10}
                  disabled={busy}
                >
                  <option value="">Select a Work ID…</option>
                  {filteredLinkWorkIds.map((w) => (
                    <option key={w.id} value={String(w.id)}>
                      {w.workIdentificationId || `#${w.id}`} – {w.status} –{' '}
                      {(w.comments || '').slice(0, 60) || 'No comments'}
                    </option>
                  ))}
                  {filteredLinkWorkIds.length === 0 && (
                    <option disabled value="">
                      {linkWorkIdSearch.trim()
                        ? 'No work IDs match your search'
                        : 'No work IDs available'}
                    </option>
                  )}
                </Form.Select>
                <Form.Text className="tw-text-content-muted tw-block tw-mt-1">
                  {filteredLinkWorkIds.length} work ID{filteredLinkWorkIds.length !== 1 ? 's' : ''}{' '}
                  shown
                  {linkWorkIdSearch.trim() ? ' (filtered)' : ''}.
                </Form.Text>
              </>
            )}
          </Form.Group>
          {selectedLinkWorkIdRow && (
            <div className="tw-border tw-border-line tw-rounded tw-p-2 tw-mt-2 tw-bg-surface-alt tw-text-sm">
              <strong>Selected:</strong>{' '}
              {selectedLinkWorkIdRow.workIdentificationId || `#${selectedLinkWorkIdRow.id}`}{' '}
              <Badge bg="secondary" className="tw-ml-1">{selectedLinkWorkIdRow.status}</Badge>
              {selectedLinkWorkIdRow.comments && (
                <div className="tw-text-content-muted tw-mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedLinkWorkIdRow.comments.slice(0, 200)}
                  {selectedLinkWorkIdRow.comments.length > 200 ? '…' : ''}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLinkModal(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleBulkLink()}
            disabled={busy || !linkSelectedWorkId || linkWorkIdListLoading}
          >
            {busy ? <Spinner animation="border" size="sm" className="tw-mr-1" /> : null}
            Link {count} PCR{count !== 1 ? 's' : ''}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PcrBulkActionsToolbar;
